import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Card, Menu, Popover, Spin, Tabs } from "antd";
import type { ItemType } from "antd/lib/menu/hooks/useItems";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import {
  OrganizationMutableProperties,
  useDeleteOrganization,
  useOrganization,
  useUpdateOrganization,
} from "../../api";
import { ActionsIcon } from "../../components/ActionsIcon";
import { AssignBeaconsModalOrganization } from "../../components/configuration/AssignBeaconsModal";
import { AssignSCBOneDevicesModal } from "../../components/configuration/AssignSCBOneDevicesModal";
import { DataConsumersTable } from "../../components/integration-platform/data-consumer/DataConsumersTable";
import { OrganizationPropertyModal } from "../../components/OrganizationPropertyModal";
import { OrganizationUsersTable } from "../../components/user/UserTable";
import { useActiveUserRoles } from "../../util/useActiveUserRoles";
import { AssignUsersModal } from "./AssignUsersModal";
import { OrganizationBeaconTable } from "./OrganizationBeaconTable";
import { OrganizationSCBOneDevicesTable } from "./OrganizationSCBOneDeviceTable";
import { OrganizationUserTableFooter } from "./OrganizationUserTableFooter";
import { Role } from "../../contexts/authContext";

export function Organization() {
  const { t } = useTranslation();
  const history = useHistory();

  const { organizationId } = useParams<{ organizationId: string }>();
  const organization = useOrganization(organizationId);

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isMounted = useRef(true);
  const [activeTab, setActiveTab] = useState<
    "users" | "beacons" | "devices" | "data-consumers"
  >("users");

  const deleteOrganization = useDeleteOrganization(organizationId);

  const {
    isSolutionOperator,
    isSCBOneDataAnalyst,
    isRoadSafetyManager,
    roles,
  } = useActiveUserRoles();

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  const onDelete = async () => {
    if (isMounted.current) setIsDeleting(true);
    try {
      await deleteOrganization();
      if (isMounted.current) history.push("/organizations");
    } catch {
      console.error("Failed to delete organization");
    } finally {
      if (isMounted.current) setIsDeleting(false);
    }
  };

  const update = useUpdateOrganization(organizationId);
  const onSaveEditing = useCallback(
    async (org: OrganizationMutableProperties) => {
      await update(org);
      organization.refresh();
    },
    [update, organization.refresh]
  );

  if (organization.loading || !organization.value || isDeleting) {
    return <Spin className="centered" />;
  }

  const usersTable = () => (
    <OrganizationUsersTable
      organizationId={organizationId}
      selectedUserIds={selectedUserIds}
      setSelectedUserIds={
        isSCBOneDataAnalyst || isRoadSafetyManager
          ? undefined
          : setSelectedUserIds
      }
      hideFooter={isSCBOneDataAnalyst || isRoadSafetyManager}
      footer={
        <OrganizationUserTableFooter
          {...{ organizationId, selectedUserIds }}
          onClearSelectedUserIds={() => setSelectedUserIds([])}
        />
      }
    />
  );

  const beaconsTable = () => {
    return <OrganizationBeaconTable organizationId={organizationId} />;
  };

  const scbOneBDevicesTable = () => {
    return <OrganizationSCBOneDevicesTable organizationId={organizationId} />;
  };

  const DataConsumersOrganizationTable = () => {
    return organization.value?.id ? (
      <DataConsumersTable organizationId={organization.value.id} />
    ) : null;
  };

  const tabItems = (userRoles: Role[]) => {
    const showUserAndDataConsumerTabs =
      userRoles.includes("integration-platform-user") ||
      userRoles.includes("road-safety-manager") ||
      userRoles.includes("scb-one-data-analyst");

    const showAllTabs = userRoles.includes("solution-operator");

    if (showUserAndDataConsumerTabs) {
      return [
        {
          key: "users",
          label: t("users"),
          children: usersTable(),
        },
        {
          key: "data-consumers",
          label: t("integrations:data-consumers-title"),
          children: DataConsumersOrganizationTable(),
        },
      ];
    } else if (showAllTabs) {
      return [
        {
          key: "users",
          label: t("users"),
          children: usersTable(),
        },
        {
          key: "beacons",
          label: t("beacons"),
          children: beaconsTable(),
        },
        {
          key: "devices",
          label: "SCB-One",
          children: scbOneBDevicesTable(),
        },
        {
          key: "data-consumers",
          label: t("integrations:data-consumers-title"),
          children: DataConsumersOrganizationTable(),
        },
      ];
    } else {
      return [
        {
          key: "users",
          label: t("users"),
          children: usersTable(),
        },
      ];
    }
  };

  const actionsMenuItems: ItemType[] = [
    {
      key: "delete",
      label: (
        <Button danger type="primary" onClick={onDelete}>
          <DeleteOutlined /> {t("delete")}
        </Button>
      ),
      onClick: () => setPopoverOpen(false),
    },
    {
      key: "edit",
      label: (
        <OrganizationPropertyModal
          onSave={onSaveEditing}
          modalButtonIcon={<EditOutlined />}
          title={t("edit")}
          organization={organization.value}
        />
      ),
      onClick: () => setPopoverOpen(false),
    },
    {
      key: "assign-users",
      label: <AssignUsersModal organizationId={organizationId} />,
      onClick: () => setPopoverOpen(false),
    },
    {
      key: "assign-beacons",
      label: <AssignBeaconsModalOrganization organizationId={organizationId} />,
      onClick: () => setPopoverOpen(false),
    },
    {
      key: "assign-devices",
      label: <AssignSCBOneDevicesModal organizationId={organizationId} />,
      onClick: () => setPopoverOpen(false),
    },
  ].filter(Boolean) as ItemType[];

  const actionsMenu = (
    <Menu
      className="actions-menu"
      style={{ minWidth: 180 }}
      items={actionsMenuItems}
    />
  );

  return (
    <div className="screen-with-breadcrumb">
      <div className="screen-with-filter-header">
        <Breadcrumb>
          <Breadcrumb.Item>
            <a
              href="/organizations"
              onClick={(e) => {
                e.preventDefault();
                history.push("/organizations");
              }}
            >
              {t("organizations")}
            </a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{organization.value.name}</Breadcrumb.Item>
        </Breadcrumb>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          gridRowGap: "1em",
        }}
      >
        <Card
          title={
            <div
              style={{ display: "grid", gridTemplateColumns: "auto 1fr auto" }}
            >
              <span>{t("organization")}</span>
              <span></span>
              {isSolutionOperator && (
                <Popover
                  content={actionsMenu}
                  trigger="click"
                  open={popoverOpen}
                  onOpenChange={(open) => setPopoverOpen(open)}
                  placement="bottomRight"
                  overlayInnerStyle={{ padding: 0 }}
                >
                  <Button>
                    <ActionsIcon />
                  </Button>
                </Popover>
              )}
            </div>
          }
        >
          <div className="property-list one">
            <span className="property-label">{t("organization:name")}</span>
            <span className="property-value">{organization.value.name}</span>
          </div>
        </Card>
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as any)}
          items={tabItems(roles)}
          style={{ margin: "8px" }}
        />
      </div>
    </div>
  );
}
